/**
 * Jurisdiction Suggestion Utility
 * Phase 4: Property Address Integration
 * 
 * Provides smart jurisdiction suggestions based on property county
 *
 * P0 STATE-COLLISION FIX (May 5, 2026): The matcher now requires `propertyState`
 * to disambiguate same-named counties across states (e.g., Knox County exists
 * in OH, TN, IN, IL, KY, ME, MO, NE, TX). Same fix class as the prior backend
 * Monroe County IN/IL fix — extended to the frontend auto-suggest path here.
 */

import { canonicalizeJurisdictionName } from './jurisdictionCanonical';

/**
 * Find matching jurisdiction based on property county AND state.
 *
 * Match precedence (when `propertyState` is provided):
 *   State + Type + Canonical Name (canonical equality, format-tolerant)
 *   State + Type + name-contains (legacy fallback within the same state)
 *
 * If `propertyState` is provided but no candidate has that state, returns
 * `null` (caller's "no-match" banner takes over, keeping the user in control).
 *
 * If `propertyState` is missing/empty (legacy callers with no state context),
 * falls back to the prior name-only behavior with a console warning.
 *
 * @param {string} propertyCounty - County from property address (e.g., "Knox")
 * @param {Array}  jurisdictions  - List of active jurisdictions (any/all states)
 * @param {string} [propertyState] - 2-letter state code from the order (e.g., "OH")
 * @returns {Object|null} - Suggested jurisdiction or null
 */
export const findJurisdictionByCounty = (propertyCounty, jurisdictions, propertyState) => {
  if (!propertyCounty || !jurisdictions || jurisdictions.length === 0) {
    return null;
  }

  const normalizedCounty = propertyCounty.trim().toLowerCase();
  const canonicalCounty = canonicalizeJurisdictionName(propertyCounty);
  const normalizedState = (propertyState || '').trim().toUpperCase();

  // Step 1: Type filter (county-level)
  let countyCandidates = jurisdictions.filter(j => {
    const jurisdictionType = (j.jurisdiction_type || j.type || '').toLowerCase();
    return jurisdictionType === 'county';
  });

  // Step 2: State filter — REQUIRED whenever a state is provided. Prevents
  // cross-state collisions like Knox County, OH vs Knox County, TN.
  if (normalizedState) {
    const stateScoped = countyCandidates.filter(j => {
      const js = (j.state || '').trim().toUpperCase();
      return js === normalizedState;
    });
    if (stateScoped.length === 0) {
      // Fail-closed: state was specified but no county matches that state.
      // Surface "no match" instead of silently returning a wrong-state record.
      return null;
    }
    countyCandidates = stateScoped;
  } else if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      '[findJurisdictionByCounty] called without propertyState — name-only ' +
      'match may pick wrong-state record for ambiguous county names.'
    );
  }

  // Step 3a: canonical equality (format-tolerant, scoped to filtered set)
  if (canonicalCounty) {
    const canonicalMatch = countyCandidates.find(j => {
      const jc = j.canonical_name || canonicalizeJurisdictionName(j.jurisdiction_name || j.name);
      return jc && jc === canonicalCounty;
    });
    if (canonicalMatch) return canonicalMatch;
  }

  // Step 3b: legacy case-insensitive contains on display name (within state scope)
  const matches = countyCandidates.filter(j => {
    const jurisdictionName = (j.jurisdiction_name || j.name || '').toLowerCase();
    return jurisdictionName.includes(normalizedCounty);
  });

  return matches.length > 0 ? matches[0] : null;
};

/**
 * Get suggestion banner message based on various states
 * 
 * @param {Object} params
 * @param {Object|null} params.suggestedJurisdiction - Suggested jurisdiction from findJurisdictionByCounty
 * @param {string|null} params.propertyCounty - County from property
 * @param {string|null} params.currentJurisdictionId - Currently selected jurisdiction ID
 * @param {Array} params.jurisdictions - All jurisdictions
 * @returns {Object} - { type: 'suggestion'|'no-match'|'update'|'none', message: string, jurisdiction: Object|null }
 */
export const getSuggestionBannerState = ({
  suggestedJurisdiction,
  propertyCounty,
  currentJurisdictionId,
  jurisdictions
}) => {
  // Case 1: No property county - skip silently
  if (!propertyCounty || propertyCounty.trim() === '') {
    return { type: 'none', message: null, jurisdiction: null };
  }

  // Case 2: No matching jurisdiction found
  if (!suggestedJurisdiction) {
    return {
      type: 'no-match',
      message: `No jurisdiction found for county: ${propertyCounty}. Please select manually.`,
      jurisdiction: null
    };
  }

  // Case 3: User already selected a jurisdiction (respect user choice)
  if (currentJurisdictionId) {
    // Check if selected jurisdiction is different from suggested
    if (currentJurisdictionId !== suggestedJurisdiction.id) {
      const jurisdictionName = suggestedJurisdiction.jurisdiction_name || suggestedJurisdiction.name || 'Unknown';
      
      return {
        type: 'update',
        message: `Property county suggests: ${jurisdictionName}. Update selection?`,
        jurisdiction: suggestedJurisdiction
      };
    } else {
      // Selected jurisdiction matches suggestion - no banner needed
      return { type: 'none', message: null, jurisdiction: null };
    }
  }

  // Case 4: Show fresh suggestion (no current selection)
  const jurisdictionName = suggestedJurisdiction.jurisdiction_name || suggestedJurisdiction.name || 'Unknown';
  const jurisdictionType = suggestedJurisdiction.jurisdiction_type || suggestedJurisdiction.type || 'Unknown';
  
  return {
    type: 'suggestion',
    message: `Suggested: ${jurisdictionName} (${jurisdictionType}) based on property address.`,
    jurisdiction: suggestedJurisdiction
  };
};

/**
 * Format jurisdiction display name
 * @param {Object} jurisdiction
 * @returns {string}
 */
export const formatJurisdictionDisplay = (jurisdiction) => {
  if (!jurisdiction) return '';
  const name = jurisdiction.jurisdiction_name || jurisdiction.name || 'Unknown';
  const type = jurisdiction.jurisdiction_type || jurisdiction.type || 'Unknown';
  return `${name} (${type})`;
};
